import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  Recycle,
  ArrowRight,
  TrendingUp,
  Award,
  Users,
  Loader2,
  Activity,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function GestionResumen() {
  const [incStats, setIncStats] = useState(null);
  const [ecoStats, setEcoStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    Promise.allSettled([
      fetch(`${BACKEND_URL}/api/incidencias/stats`).then((r) => (r.ok ? r.json() : null)),
      fetch(`${BACKEND_URL}/api/ecoparque/stats`).then((r) => (r.ok ? r.json() : null)),
    ]).then(([i, e]) => {
      if (!alive) return;
      setIncStats(i.status === 'fulfilled' ? i.value : null);
      setEcoStats(e.status === 'fulfilled' ? e.value : null);
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className="space-y-6" data-testid="gestion-resumen">
      <div>
        <h2 className="font-display text-2xl text-[#0A2540]">Bienvenido a tu portal de Gestión Interna</h2>
        <p className="text-sm text-[#6F8AA8] mt-1">
          Acceso unificado a la operación diaria: incidencias del municipio (capa GIS) y gestión del Ecoparque (PAYT).
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Card Incidencias */}
        <Link
          to="/gestion/incidencias"
          className="group rounded-3xl border border-[#CFE3F5] bg-white p-6 shadow-sm hover:shadow-md transition-shadow"
          data-testid="resumen-card-incidencias"
        >
          <div className="flex items-start justify-between mb-5">
            <div className="w-12 h-12 rounded-2xl bg-[rgba(194,65,12,0.12)] flex items-center justify-center">
              <AlertTriangle size={22} className="text-[#C2410C]" />
            </div>
            <ArrowRight
              size={20}
              className="text-[#6F8AA8] group-hover:text-[#1E5AA8] group-hover:translate-x-0.5 transition-all"
            />
          </div>
          <h3 className="font-display text-2xl text-[#0A2540] mb-1">Incidencias</h3>
          <p className="text-sm text-[#6F8AA8] mb-5 leading-relaxed">
            Bandeja de avisos sincronizada con la capa <strong>ArcGIS</strong>. Mapa, KPIs, histórico, gráficos
            por estado y categoría, y trazabilidad completa de respuestas a la ciudadanía.
          </p>
          <div className="grid grid-cols-3 gap-3">
            <MiniKPI
              label="Total"
              value={loading ? '…' : incStats?.total ?? '—'}
              testid="resumen-inc-total"
            />
            <MiniKPI
              label="Nuevas"
              value={loading ? '…' : incStats?.nuevas ?? '—'}
              color="#EF4444"
              testid="resumen-inc-nuevas"
            />
            <MiniKPI
              label="En proceso"
              value={loading ? '…' : incStats?.en_proceso ?? '—'}
              color="#F59E0B"
              testid="resumen-inc-proceso"
            />
          </div>
        </Link>

        {/* Card Ecoparque */}
        <Link
          to="/gestion/ecoparque"
          className="group rounded-3xl border border-[#CFE3F5] bg-white p-6 shadow-sm hover:shadow-md transition-shadow"
          data-testid="resumen-card-ecoparque"
        >
          <div className="flex items-start justify-between mb-5">
            <div className="w-12 h-12 rounded-2xl bg-[rgba(31,138,112,0.12)] flex items-center justify-center">
              <Recycle size={22} className="text-[#1F8A70]" />
            </div>
            <ArrowRight
              size={20}
              className="text-[#6F8AA8] group-hover:text-[#1F8A70] group-hover:translate-x-0.5 transition-all"
            />
          </div>
          <h3 className="font-display text-2xl text-[#0A2540] mb-1">Ecoparque</h3>
          <p className="text-sm text-[#6F8AA8] mb-5 leading-relaxed">
            Dashboard PAYT del Punto Limpio: kg por tipo de residuo, evolución, top ciudadanos,
            comparativa mensual, mapa de orígenes y registro de entregas con QR.
          </p>
          <div className="grid grid-cols-3 gap-3">
            <MiniKPI
              label="Total kg"
              value={loading ? '…' : ecoStats?.total_kg?.toFixed?.(1) ?? '0.0'}
              color="#1F8A70"
              testid="resumen-eco-kg"
            />
            <MiniKPI
              label="Entregas"
              value={loading ? '…' : ecoStats?.total_entries ?? 0}
              color="#1E5AA8"
              testid="resumen-eco-entries"
            />
            <MiniKPI
              label="Ciudadanos"
              value={loading ? '…' : ecoStats?.active_users ?? 0}
              color="#0A2540"
              testid="resumen-eco-users"
            />
          </div>
        </Link>
      </div>

      {/* Quick stats fila inferior */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <QuickStat
          icon={Activity}
          label="Incidencias completadas"
          value={loading ? '…' : incStats?.completadas ?? '—'}
          color="#1F8A70"
        />
        <QuickStat
          icon={Award}
          label="Puntos PAYT repartidos"
          value={loading ? '…' : (ecoStats?.total_points ?? 0).toLocaleString('es-ES')}
          color="#1E5AA8"
        />
        <QuickStat
          icon={Users}
          label="Ciudadanos registrados"
          value={loading ? '…' : ecoStats?.total_users ?? 0}
          color="#7DB9E8"
        />
        <QuickStat
          icon={TrendingUp}
          label="Δ kg vs mes anterior"
          value={
            loading
              ? '…'
              : ecoStats?.comparison?.delta_kg_pct == null
              ? '—'
              : `${ecoStats.comparison.delta_kg_pct > 0 ? '+' : ''}${ecoStats.comparison.delta_kg_pct}%`
          }
          color={
            !ecoStats?.comparison?.delta_kg_pct
              ? '#6F8AA8'
              : ecoStats.comparison.delta_kg_pct >= 0
              ? '#1F8A70'
              : '#C2410C'
          }
        />
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-[#6F8AA8]">
          <Loader2 size={14} className="animate-spin" /> Cargando KPIs…
        </div>
      )}
    </div>
  );
}

function MiniKPI({ label, value, color = '#0A2540', testid }) {
  return (
    <div className="rounded-2xl bg-[#FAFCFE] border border-[#CFE3F5] px-3 py-2.5 text-center">
      <p className="text-[10px] uppercase tracking-wider text-[#6F8AA8]">{label}</p>
      <p className="font-display text-xl tabular-nums" style={{ color }} data-testid={testid}>
        {value}
      </p>
    </div>
  );
}

function QuickStat({ icon: Icon, label, value, color }) {
  return (
    <div className="rounded-2xl bg-white border border-[#CFE3F5] p-4 flex items-center gap-3">
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: `${color}1f` }}
      >
        <Icon size={18} style={{ color }} />
      </div>
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wider text-[#6F8AA8] truncate">{label}</p>
        <p className="font-display text-lg text-[#0A2540] tabular-nums">{value}</p>
      </div>
    </div>
  );
}
